import React, { createContext, useCallback, useContext, useState } from "react";
import { 
  getListaWarehouseClientPaginated,
  WarehouseClientResponse, 
  WarehouseClientVM,
  WarehouseClientPutVM,
  WarehouseClientApiResponse,
  WarehouseClientApiCreateResponse, 
  getListaWarehouseClient, 
  getWarehouseClientById, 
  createWarehouseClient, 
  updateWarehouseClient, 
  deleteWarehouseClient 
} from "../../services/warehouseClientService";
import { getErrorMessage } from "../../locales/translationUtils";
import LoadingOverlay from "../../components/Loading";
import Paginacao from "../../types/paginacao";
import { ApiResponse } from "../../services/api";
import warehouseClient from "../../types/warehouseClient";

interface WarehouseClientContextProps {
  apiResponse: ApiResponse<warehouseClient> | null;
  warehouseClient: WarehouseClientResponse[] | warehouseClient[] | null;
  warehouseClientById: WarehouseClientApiResponse | null;
  loading: boolean;
  error: string | null;
  fetchWarehouseClient: () => Promise<WarehouseClientResponse[]>;
  fetchWarehouseClientPaginated: (paginacao: Paginacao) => void;
  fetchWarehouseClientById: (id: number) => Promise<WarehouseClientApiResponse | null>;
  postWarehouseClient: (cargo: WarehouseClientVM) => Promise<WarehouseClientApiCreateResponse | null>;
  putWarehouseClient: (id: number, cargo: WarehouseClientPutVM) => Promise<WarehouseClientApiResponse | null>;
  excluiWarehouseClient: (id: number) => Promise<boolean>;
}

interface WarehouseClientProviderProps {
  children: React.ReactNode;
}

const WarehouseClientContext = createContext<WarehouseClientContextProps | null>(null);

const WarehouseClientProvider: React.FC<WarehouseClientProviderProps> = ({ children }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse<warehouseClient> | null>(null);
  const [warehouseClient, setWarehouseClient] = useState<WarehouseClientResponse[] | warehouseClient[] | null>(null);
  const [warehouseClientById, setWarehouseClientById] = useState<WarehouseClientApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchWarehouseClient = useCallback(async (): Promise<WarehouseClientResponse[]> => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaWarehouseClient();
      const values = response.values || [];
      setWarehouseClient(values);
      return values;
    } catch (err) {
      setError(getErrorMessage("fetchWarehouseClientError"));
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchWarehouseClientPaginated = useCallback(async (paginacao: Paginacao) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getListaWarehouseClientPaginated(paginacao);
      setApiResponse(response);
      setWarehouseClient(response.data.values || []);
    } catch (err) {
      setWarehouseClient([]);
      setError(getErrorMessage("fetchBranchError"));
    } finally {
      setLoading(false);
    }
  }, []);
  

  const fetchWarehouseClientById = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      const response = await getWarehouseClientById(id);
      setWarehouseClientById(response);
      return response;
    } catch (err) {
      setError(getErrorMessage("fetchWarehouseClientByIdError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const postWarehouseClient = useCallback(async (cargo: WarehouseClientVM) => {
    setError(null);
    try {
      setLoading(true);
      const response = await createWarehouseClient(cargo);
      fetchWarehouseClient();
      return response;
    } catch (err) {
      setError(getErrorMessage("createWarehouseClientError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWarehouseClient]);

  const putWarehouseClient = useCallback(async (id: number, cargo: WarehouseClientPutVM) => {
    setError(null);
    try {
      setLoading(true);
      const response = await updateWarehouseClient(id, cargo);
      fetchWarehouseClient();
      return response;
    } catch (err) {
      setError(getErrorMessage("updateWarehouseClientError"));
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchWarehouseClient]);

  const excluiWarehouseClient = useCallback(async (id: number) => {
    setError(null);
    try {
      setLoading(true);
      await deleteWarehouseClient(id);
      fetchWarehouseClient();
      return true;
    } catch (err) {
      setError(getErrorMessage("deleteWarehouseClientError"));
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchWarehouseClient]);

  return (
    <WarehouseClientContext.Provider 
      value={{ 
        apiResponse,
        fetchWarehouseClientPaginated,
        warehouseClient, 
        warehouseClientById, 
        loading, 
        error, 
        fetchWarehouseClient, 
        fetchWarehouseClientById, 
        postWarehouseClient, 
        putWarehouseClient, 
        excluiWarehouseClient 
      }}
    >
      <LoadingOverlay isLoading={loading} />
      {children}
    </WarehouseClientContext.Provider>
  );
};

export default WarehouseClientProvider;

export const useWarehouseClient = (): WarehouseClientContextProps => {
  const context = useContext(WarehouseClientContext);
  if (!context) {
    throw new Error(
      getErrorMessage("wrongUseProviderError", {
        auth: useWarehouseClient?.name,
        providerName: WarehouseClientProvider?.name,
      })
    );
  }
  return context;
};
